import { useDashboardAudience } from './dashboardAudience'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Navigate } from 'react-router-dom'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Alert } from '@/components/ui/alert'
import { EmptyState } from '@/components/ui/empty-state'
import { Button } from '@/components/ui/button'
import { ApiError, getJSON } from '@/lib/api'
import type {
  PortalRegistrationEvent,
  PortalRegistrationPeople,
} from './portalEventRegistrations.types'
import { PortalEventPeopleTable } from './PortalEventPeopleTable'

export function PortalEventPeopleSheet({
  organizationId,
  viewer,
  row,
  trigger,
  onClose,
}: {
  organizationId: number
  viewer: string
  row: PortalRegistrationEvent
  trigger: HTMLElement
  onClose: () => void
}) {
  const audience = useDashboardAudience(organizationId)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState('person:asc')
  const params = new URLSearchParams({ sort })
  if (search) params.set('q', search)
  const path = `${audience.base}/event_registrations/${encodeURIComponent(row.event.id)}/people?${params}`
  const query = useQuery({
    queryKey: [
      audience.preview ? 'organization-dashboard-preview' : 'member-portal-dashboard',
      viewer,
      organizationId,
      'event-people',
      path,
    ],
    queryFn: () => getJSON<{ registration_people: PortalRegistrationPeople }>(path),
    retry: false,
  })
  const people = query.isError ? undefined : query.data?.registration_people
  if (!audience.preview && query.error instanceof ApiError && query.error.status === 404)
    return <Navigate to="/portal" replace />
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <SheetContent
        className="w-full overflow-hidden px-3 focus:outline-none sm:max-w-3xl sm:px-6"
        tabIndex={-1}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          ;(event.currentTarget as HTMLElement).focus()
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          if (trigger.isConnected) trigger.focus()
        }}
      >
        <SheetHeader className="shrink-0 pb-0 pr-8">
          <SheetTitle>{row.event.name}</SheetTitle>
          <SheetDescription>{row.description}</SheetDescription>
        </SheetHeader>
        <Input
          aria-label="Search people"
          placeholder="Search people"
          className="shrink-0"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <div
          role="region"
          aria-label="People list"
          className="min-h-0 min-w-0 flex-1 overflow-y-auto"
        >
          {query.isFetching ? (
            <p role="status">Loading people…</p>
          ) : query.isError ? (
            <Alert variant="destructive">
              People unavailable.{' '}
              <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
                Try again
              </Button>
            </Alert>
          ) : (
            people &&
            (people.empty_message ? (
              <EmptyState variant="quiet" title={people.empty_message} />
            ) : (
              <PortalEventPeopleTable
                people={people}
                started={row.has_started}
                sort={sort}
                onSort={setSort}
              />
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
